import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { OrganizationProvider, useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import Sidebar from '../common/sidebar';
import TopBar from '../common/topbar';
import AnalystSpreadsheet from './AnalystSpreadsheet';
import AnalystChat from './AnalystChat';
import AnalystGallery from './AnalystGallery';
import AnalystTabBar from './AnalystTabBar';
import AnalystNewSession from './AnalystNewSession';
import { createThread } from '../../../services/threadService';
import {
  createSession,
  updateSession as updateSessionApi,
  addSheet as addSheetApi,
  updateSheet as updateSheetApi,
  removeSheet as removeSheetApi,
} from '../../../services/analystSessionService';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { saveSheetData, loadSheetData, deleteSheetData, deleteSessionData } from '../../../services/sheetStorageService';

const TOOLS = [
  { id: 'dashboard', label: 'Dashboard Agent' },
  { id: 'ml', label: 'ML Agent' },
  { id: 'adhoc', label: 'Ad-hoc Analyst' },
];

function columnToLetter(column) {
  let temp, letter = '', index = column + 1;
  while (index > 0) {
    temp = (index - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    index = (index - temp - 1) / 26;
  }
  return letter;
}

function formatRange(range) {
  const { fromRow, toRow, fromCol, toCol } = range;
  return `${columnToLetter(fromCol)}${fromRow + 1}:${columnToLetter(toCol)}${toRow + 1}`;
}

function autoName(jsonData, menu, submenu) {
  if (jsonData?.length > 0) {
    const keys = Object.keys(jsonData[0]).slice(0, 3);
    if (keys.length) return keys.join(', ');
  }
  if (menu) return `${menu}${submenu ? ' / ' + submenu : ''}`;
  return null;
}

// Local per-sheet runtime state (not persisted to DB)
function makeLocalSheetState() {
  return { df: [], highlightedRanges: [], conversation: [] };
}

const AnalystPageContent = () => {
  const { organizationId } = useParams();
  const { organizationData, fetchOrganizationData } = useOrganization();
  const { userInfo } = useUser();

  // --- View: 'gallery' | 'new' | 'workspace' ---
  const [view, setView] = useState('gallery');

  // --- Current session (backend-persisted) ---
  const [currentSession, setCurrentSession] = useState(null); // { _id, name, sheets: [...] }
  const [activeSheetId, setActiveSheetId] = useState(null);

  // --- Per-sheet runtime state map: sheetId → { df, highlightedRanges, conversation } ---
  const [sheetState, setSheetState] = useState({});

  const [completionLoading, setCompletionLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);
  const toolsRef = useRef(null);
  const hotRef = useRef(null);
  const chatRef = useRef(null);
  const saveTimerRef = useRef(null);

  // Loaded sheet data from IndexedDB (passed to AnalystSpreadsheet as initialData)
  const [loadedSheetData, setLoadedSheetData] = useState(null);

  // Active sheet from session
  const activeSheet = currentSession?.sheets?.find(s => s._id === activeSheetId) || currentSession?.sheets?.[0];
  const activeLocal = sheetState[activeSheetId] || makeLocalSheetState();

  // Ensure local state exists for a sheet
  const ensureLocalState = useCallback((sheetId) => {
    setSheetState(prev => {
      if (prev[sheetId]) return prev;
      return { ...prev, [sheetId]: makeLocalSheetState() };
    });
  }, []);

  // Update local state for a specific sheet
  const updateLocalState = useCallback((sheetId, updates) => {
    setSheetState(prev => ({
      ...prev,
      [sheetId]: { ...(prev[sheetId] || makeLocalSheetState()), ...updates },
    }));
  }, []);

  // Scoped setters for active sheet
  const setDf = useCallback((val) => {
    const next = typeof val === 'function' ? val(activeLocal.df) : val;
    updateLocalState(activeSheetId, { df: next });
  }, [activeSheetId, activeLocal.df, updateLocalState]);

  const setHighlightedRanges = useCallback((val) => {
    const next = typeof val === 'function' ? val(activeLocal.highlightedRanges) : val;
    updateLocalState(activeSheetId, { highlightedRanges: next });
  }, [activeSheetId, activeLocal.highlightedRanges, updateLocalState]);

  const setConversation = useCallback((val) => {
    const next = typeof val === 'function' ? val(activeLocal.conversation) : val;
    updateLocalState(activeSheetId, { conversation: next });
  }, [activeSheetId, activeLocal.conversation, updateLocalState]);

  // --- Auto-save: debounced write to IndexedDB ---
  // IMPORTANT: We snapshot hot.getData() immediately when the change fires,
  // capturing the correct data and IDs at schedule time. The debounce only
  // delays the IDB write. This prevents stale hotRef reads after tab switches.
  const handleDataChange = useCallback(() => {
    if (!currentSession?._id || !activeSheetId) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const snapshot = hot.getData();
    const sid = currentSession._id;
    const shid = activeSheetId;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveSheetData(sid, shid, snapshot).catch(err =>
        console.error('Auto-save failed:', err)
      );
    }, 1500);
  }, [currentSession?._id, activeSheetId]);

  // Save current sheet before switching away
  const flushSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const hot = hotRef.current?.hotInstance;
    if (hot && currentSession?._id && activeSheetId) {
      saveSheetData(currentSession._id, activeSheetId, hot.getData()).catch(err =>
        console.error('Flush save failed:', err)
      );
    }
  }, [currentSession?._id, activeSheetId]);

  // --- Auto-load: read from IndexedDB when active sheet changes ---
  useEffect(() => {
    if (!currentSession?._id || !activeSheetId) {
      setLoadedSheetData(null);
      return;
    }
    let cancelled = false;
    loadSheetData(currentSession._id, activeSheetId)
      .then(data => { if (!cancelled) setLoadedSheetData(data); })
      .catch(() => { if (!cancelled) setLoadedSheetData(null); });
    return () => { cancelled = true; };
  }, [currentSession?._id, activeSheetId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (organizationId) fetchOrganizationData(organizationId);
  }, [organizationId, fetchOrganizationData]);

  // --- Session lifecycle ---

  // Create a new session — show the stepper
  const handleNewSession = useCallback(() => {
    setView('new');
  }, []);

  // Stepper completed — create session on backend, load data into sheet
  // IMPORTANT: We save data to IDB BEFORE setting activeSheetId. This way the
  // auto-load useEffect (triggered by activeSheetId change) reads the data that
  // was just written, avoiding a race where the read returns null.
  const handleStepperComplete = useCallback(async ({ name, dataSource, data }) => {
    try {
      const res = await createSession(organizationId, { name });
      const session = res.data?.data || res.data;
      setCurrentSession(session);
      const firstSheetId = session.sheets[0]?._id;
      if (firstSheetId) ensureLocalState(firstSheetId);

      // Save data to IDB first so the auto-load finds it
      if (data && firstSheetId) {
        await saveSheetData(session._id, firstSheetId, data);
      }

      // Now trigger auto-load — IDB already has the data
      setLoadedSheetData(null);
      setActiveSheetId(firstSheetId);
      setView('workspace');
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  }, [organizationId, ensureLocalState]);

  // Open an existing session from the gallery
  const handleOpenSession = useCallback((session) => {
    flushSave();
    setLoadedSheetData(null);
    setCurrentSession(session);
    const firstSheetId = session.sheets?.[0]?._id;
    setActiveSheetId(firstSheetId);
    session.sheets?.forEach(s => ensureLocalState(s._id));
    setView('workspace');
  }, [ensureLocalState, flushSave]);

  const handleShowGallery = useCallback(() => {
    flushSave();
    setView('gallery');
  }, [flushSave]);

  // --- Session rename ---
  const startRename = useCallback(() => {
    setRenameValue(currentSession?.name || '');
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.select(), 0);
  }, [currentSession?.name]);

  const commitRename = useCallback(async () => {
    const trimmed = renameValue.trim();
    setIsRenaming(false);
    if (!trimmed || !currentSession?._id || trimmed === currentSession.name) return;
    try {
      await updateSessionApi(organizationId, currentSession._id, { name: trimmed });
      setCurrentSession(prev => ({ ...prev, name: trimmed }));
    } catch (err) {
      console.error('Failed to rename session:', err);
    }
  }, [renameValue, currentSession, organizationId]);

  // Rename from gallery (used by AnalystGallery)
  const handleRenameSession = useCallback(async (sessionId, newName) => {
    try {
      await updateSessionApi(organizationId, sessionId, { name: newName });
    } catch (err) {
      console.error('Failed to rename session:', err);
    }
  }, [organizationId]);

  // --- Sheet/tab management ---

  const handleSelectTab = useCallback((sheetId) => {
    flushSave();
    setLoadedSheetData(null);
    setActiveSheetId(sheetId);
    ensureLocalState(sheetId);
  }, [ensureLocalState, flushSave]);

  const handleNewTab = useCallback(async () => {
    if (!currentSession) return;
    flushSave(); // Save current sheet before switching
    try {
      const res = await addSheetApi(organizationId, currentSession._id, {});
      const updated = res.data?.session || res.data;
      setCurrentSession(updated);
      const newSheet = updated.sheets[updated.sheets.length - 1];
      setLoadedSheetData(null);
      setActiveSheetId(newSheet._id);
      ensureLocalState(newSheet._id);
    } catch (err) {
      console.error('Failed to add sheet:', err);
    }
  }, [currentSession, organizationId, ensureLocalState, flushSave]);

  const handleCloseTab = useCallback(async (sheetId) => {
    if (!currentSession || currentSession.sheets.length <= 1) return; // Keep at least 1 sheet
    // Clear any pending save for the sheet being closed
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    try {
      const res = await removeSheetApi(organizationId, currentSession._id, sheetId);
      const updated = res.data?.session || res.data;
      setCurrentSession(updated);
      // Clean up local state + IndexedDB
      setSheetState(prev => {
        const next = { ...prev };
        delete next[sheetId];
        return next;
      });
      deleteSheetData(currentSession._id, sheetId).catch(err =>
        console.error('Failed to delete sheet data from IndexedDB:', err)
      );
      if (activeSheetId === sheetId) {
        setLoadedSheetData(null); // Clear stale data before switching
        setActiveSheetId(updated.sheets[updated.sheets.length - 1]?._id);
      }
    } catch (err) {
      console.error('Failed to remove sheet:', err);
    }
  }, [currentSession, organizationId, activeSheetId]);

  // --- Spreadsheet interactions ---
  const scrollToCell = useCallback((range) => {
    const hotInstance = hotRef.current?.hotInstance;
    if (!hotInstance || !range?.length) return;
    setHighlightedRanges([...range]);
    hotInstance.scrollViewportTo(range[0].fromRow, range[0].fromCol);
    hotInstance.render();
  }, [setHighlightedRanges]);

  // --- Lazy thread creation + submit ---
  const handleOnSubmit = useCallback(async (jsonData, selectedRange, menu, submenu, prompt) => {
    if (!currentSession || !activeSheet) return;
    setCompletionLoading(true);

    // Add user message to local conversation
    setConversation(prev => [
      ...prev,
      {
        role: 'user',
        content: formatRange(selectedRange[0]),
        range: selectedRange,
        menu,
        submenu: submenu || prompt,
      },
    ]);

    // Lazy thread creation for this sheet
    let threadId = activeSheet.thread_id;
    if (!threadId) {
      try {
        const name = autoName(jsonData, menu, submenu) || activeSheet.name;
        // 1. Create LangGraph thread via existing thread API
        const threadRes = await createThread(organizationId, {
          prompt: name,
          agentType: 'analyst',
          endpoint: 'analyst',
          input_data: { menu, submenu, rowCount: jsonData?.length },
        });
        const created = threadRes.data?.data || threadRes.data;
        threadId = created.thread_id;

        // 2. Attach thread to this sheet in the session
        await updateSheetApi(organizationId, currentSession._id, activeSheet._id, {
          thread_id: created.thread_id,
          mongo_thread_id: created._id,
          name,
        });

        // 3. Update local session state
        setCurrentSession(prev => ({
          ...prev,
          sheets: prev.sheets.map(s =>
            s._id === activeSheet._id
              ? { ...s, thread_id: created.thread_id, mongo_thread_id: created._id, name }
              : s
          ),
        }));
      } catch (err) {
        console.error('Failed to create analyst thread:', err);
        setCompletionLoading(false);
        return;
      }
    }

    // Submit to LangGraph, passing the threadId explicitly
    if (AnalystChat.submitRef) {
      AnalystChat.submitRef(jsonData, selectedRange, menu, submenu, prompt, threadId);
    }

    setCompletionLoading(false);
  }, [currentSession, activeSheet, organizationId, setConversation]);

  const clearData = useCallback(() => {
    const hot = hotRef.current?.hotInstance;
    if (hot) hot.clear();
  }, []);

  if (!organizationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-pink mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  // ----- Gallery View -----
  if (view === 'gallery') {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={false} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar />
          <AnalystGallery
            organizationId={organizationId}
            onNewSession={handleNewSession}
            onOpenSession={handleOpenSession}
            onRenameSession={handleRenameSession}
            onDeleteSession={(sessionId) => {
              deleteSessionData(sessionId).catch(err =>
                console.error('Failed to clean up IndexedDB for session:', err)
              );
            }}
          />
        </div>
      </div>
    );
  }

  // ----- New Session Stepper -----
  if (view === 'new') {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={false} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar />
          <AnalystNewSession
            onComplete={handleStepperComplete}
            onCancel={() => setView('gallery')}
          />
        </div>
      </div>
    );
  }

  // Build tabs array for the tab bar from session sheets
  const tabs = (currentSession?.sheets || []).map(s => ({
    id: s._id,
    name: s.name || `Sheet ${s.order + 1}`,
    threadId: s.thread_id,
  }));

  // ----- Workspace View -----
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={false} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        {/* Session breadcrumb bar */}
        <div className="flex items-center gap-2 px-4 py-2 bg-white border-b border-gray-200">
          <button
            onClick={handleShowGallery}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand-pink transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All Sessions
          </button>
          <span className="text-gray-300">/</span>
          {isRenaming ? (
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              className="text-sm font-medium text-gray-800 bg-gray-50 border border-gray-300 rounded px-2 py-0.5 outline-none focus:border-brand-pink focus:ring-1 focus:ring-brand-pink/30 w-64"
              autoFocus
            />
          ) : (
            <button
              onClick={startRename}
              className="text-sm font-medium text-gray-800 truncate hover:text-brand-pink transition-colors cursor-text"
              title="Click to rename session"
            >
              {currentSession?.name || 'Untitled Session'}
            </button>
          )}
        </div>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Left: Spreadsheet + tabs */}
          <div className="flex flex-col flex-1 lg:w-[70%] lg:max-w-[70%]">
            <div className="flex-1 overflow-hidden m-2 mb-0 rounded-lg border border-gray-200 bg-white">
              <AnalystSpreadsheet
                key={activeSheetId}
                df={activeLocal.df}
                setDf={setDf}
                onSubmit={handleOnSubmit}
                highlightedRanges={activeLocal.highlightedRanges}
                setHighlightedRanges={setHighlightedRanges}
                completionLoading={completionLoading}
                hotRef={hotRef}
                onDataChange={handleDataChange}
                initialData={loadedSheetData}
              />
            </div>

            {/* Bottom toolbar */}
            <div className="flex items-center justify-between px-2 py-1.5">
              <div className="flex items-center gap-3">
                <button
                  onClick={clearData}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand-pink transition-colors"
                  title="Clear all data"
                >
                  <AutoDeleteIcon style={{ fontSize: '18px' }} />
                  <span className="hidden sm:inline">Clear</span>
                </button>
                <button
                  onClick={() => setHighlightedRanges([])}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand-pink transition-colors"
                  title="Clear highlights"
                >
                  <FormatColorResetIcon style={{ fontSize: '18px' }} />
                  <span className="hidden sm:inline">Reset highlights</span>
                </button>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative" ref={toolsRef}>
                  <button
                    onClick={() => setToolsOpen(prev => !prev)}
                    className="flex items-center gap-0.5 text-xs text-gray-500 hover:text-brand-pink transition-colors"
                  >
                    Tools
                    <KeyboardArrowDownIcon style={{ fontSize: '16px' }} className={`transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {toolsOpen && (
                    <div className="absolute bottom-full mb-1 right-0 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {TOOLS.map(t => (
                        <button
                          key={t.id}
                          className="w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-brand-pink transition-colors cursor-default"
                          title="Coming soon"
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-gray-300">|</span>
                <a
                  href="https://docs.google.com/spreadsheets/d/140U78aP6FrkIgNuE-gN2WNpAQ-Q1SXowL12y-hOc7-A/edit?usp=sharing"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-brand-pink hover:text-brand-pink/70 transition-colors no-underline"
                >
                  Test Data ↗
                </a>
              </div>
            </div>

            {/* Worksheet tab bar */}
            <AnalystTabBar
              tabs={tabs}
              activeTabId={activeSheetId}
              onSelectTab={handleSelectTab}
              onCloseTab={handleCloseTab}
              onNewTab={handleNewTab}
              onShowGallery={handleShowGallery}
            />
          </div>

          {/* Right: Chat Panel */}
          <div className="lg:w-[30%] lg:min-w-[30%] border-l border-gray-200 bg-white flex flex-col overflow-hidden">
            <div className="px-3 py-2.5 border-b border-gray-200 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-brand-pink"></div>
              <h3 className="text-sm font-medium text-gray-800 truncate">
                {currentSession?.name || 'Analyst'}
              </h3>
              {activeSheet?.thread_id && (
                <span className="text-[9px] text-gray-400 font-mono">{activeSheet.thread_id.slice(0, 8)}</span>
              )}
              {isStreaming && (
                <span className="ml-auto text-[10px] text-brand-pink font-medium">streaming...</span>
              )}
            </div>

            {activeLocal.conversation.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center max-w-xs">
                  <div className="w-12 h-12 rounded-full bg-brand-pink/10 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-brand-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 font-medium mb-1">Select data to analyze</p>
                  <p className="text-xs text-gray-400">
                    Paste data into the spreadsheet, select cells, then right-click to choose an analysis type.
                  </p>
                </div>
              </div>
            ) : (
              <AnalystChat
                key={activeSheetId}
                ref={chatRef}
                conversation={activeLocal.conversation}
                onScrollToCell={scrollToCell}
                threadId={activeSheet?.thread_id}
                isStreaming={isStreaming}
                setIsStreaming={setIsStreaming}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AnalystPage = () => {
  return (
    <OrganizationProvider>
      <AnalystPageContent />
    </OrganizationProvider>
  );
};

export default AnalystPage;

